import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { Download, Upload, Plus, Pencil, Trash2, Wrench, DatabaseBackup } from "lucide-react";
import { useLang } from "@/context/LanguageContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { API, ax, errorText } from "./common";
import FileBackupPanel from "./FileBackupPanel";

const BASE = `${API}/admin/db-backup`;
const MAX_EVERY = { day: 31, week: 54, month: 12 };
const NEW_RULE ={ enabled: true, unit: "day", every: 1, time: "20:00", weekday: 6, day: 1, keep: 7 };
const inRange = (v, lo, hi) => Number.isInteger(Number(v)) && Number(v) >= lo && Number(v) <= hi;
const card = "bg-white rounded-2xl border border-slate-200 shadow-sm p-4 space-y-3";

function saveBlob(blob, name) {
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = name;
  a.click();
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}

// Zip downloads come back as a Blob, so the JSON error has to be read out of it.
async function blobErrorText(e, fallback) {
  try { return JSON.parse(await e.response.data.text()).detail || fallback; } catch { return fallback; }
}

// Platform administrator only: manual export/restore, maintenance mode, automatic backups to the R2 backup bucket.
export default function DbBackupTab({ showMsg, refreshTick = 0 }) {
  const { t, lang } = useLang();
  const [status, setStatus] = useState(null);
  const [exportPassword, setExportPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const [restore, setRestore] = useState({ file: null, password: "", zipPassword: "" });
  const [preview, setPreview] = useState(null);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [form, setForm] = useState(null);
  const [editingId, setEditingId] = useState(null);

  const when = (iso) => (iso ? new Date(iso).toLocaleString(lang === "tr" ? "tr-TR" : "en-GB", { dateStyle: "medium", timeStyle: "short" }) : "—");

  const load = useCallback(async () => {
    try {
      const { data } = await axios.get(BASE, ax);
      setStatus(data);
    } catch (e) { showMsg("error", errorText(e, t("adm.error"))); }
  }, [showMsg, t]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { if (refreshTick) load(); }, [refreshTick]); // eslint-disable-line react-hooks/exhaustive-deps

  const run = async (fn) => {
    setBusy(true);
    try { await fn(); } finally { setBusy(false); }
  };

  const exportZip = () => run(async () => {
    try {
      const { data } = await axios.post(`${BASE}/export`, { password: exportPassword }, { ...ax, responseType: "blob" });
      saveBlob(data, `boxprep-backup-${new Date().toISOString().slice(0, 19).replace(/[-:]/g, "")}.zip`);
      setExportPassword("");
      showMsg("success", t("adm.db.exported"));
    } catch (e) { showMsg("error", await blobErrorText(e, t("adm.error"))); }
  });

  const download = (key) => run(async () => {
    try {
      const { data } = await axios.get(`${BASE}/files/${key}`, { ...ax, responseType: "blob" });
      saveBlob(data, key.replaceAll("/", "-"));
    } catch (e) { showMsg("error", await blobErrorText(e, t("adm.error"))); }
  });

  const setMaintenance = (on) => run(async () => {
    try {
      await axios.put(`${BASE}/maintenance`, { on }, ax);
      setPreview(null);
      showMsg("success", t(on ? "adm.db.maint_turned_on" : "adm.db.maint_turned_off"));
    } catch (e) { showMsg("error", errorText(e, t("adm.error"))); }
    load();
  });

  const restoreForm = (extra = {}) => {
    const body = new FormData();
    body.append("file", restore.file);
    body.append("password", restore.password);
    body.append("zip_password", restore.zipPassword);
    Object.entries(extra).forEach(([k, v]) => body.append(k, v));
    return body;
  };

  const runPreview = () => run(async () => {
    try {
      const { data } = await axios.post(`${BASE}/restore/preview`, restoreForm(), ax);
      setPreview(data);
    } catch (e) { setPreview(null); showMsg("error", errorText(e, t("adm.error"))); }
  });

  const applyRestore = () => run(async () => {
    setConfirmOpen(false);
    try {
      const { data } = await axios.post(`${BASE}/restore`, restoreForm({ confirm: preview.confirm }), ax);
      showMsg("success", t("adm.db.restored", { n: data.restored, key: data.safety_copy }));
      setRestore({ file: null, password: "", zipPassword: "" });
    } catch (e) { showMsg("error", errorText(e, t("adm.error"))); }
    setPreview(null);
    load();
  });

  const backupNow = () => run(async () => {
    try {
      await axios.post(`${BASE}/backup-now`, {}, ax);
      showMsg("success", t("adm.db.saved"));
    } catch (e) { showMsg("error", errorText(e, t("adm.error"))); }
    load();
  });

  const openRule = (rule) => {
    setEditingId(rule?.id || null);
    setForm(rule ? { ...NEW_RULE, ...rule } : NEW_RULE);
  };

  const saveRule = () => run(async () => {
    const body = { ...form, every: Number(form.every), weekday: Number(form.weekday), day: Number(form.day), keep: Number(form.keep) };
    ["id", "created_at", "last_run_at", "last_status", "last_error", "last_key", "next_run_at"].forEach(k => delete body[k]);
    try {
      if (editingId) await axios.put(`${BASE}/rules/${editingId}`, body, ax);
      else await axios.post(`${BASE}/rules`, body, ax);
      setForm(null);
    } catch (e) { showMsg("error", errorText(e, t("adm.error"))); }
    load();
  });

  const deleteRule = (rule) => run(async () => {
    try { await axios.delete(`${BASE}/rules/${rule.id}`, ax); } catch (e) { showMsg("error", errorText(e, t("adm.error"))); }
    load();
  });

  const describe = (r) => {
    const time = r.time;
    if (r.unit === "day") return r.every === 1 ? t("adm.db.rule_daily", { time }) : t("adm.db.rule_days", { n: r.every, time });
    if (r.unit === "week") return t("adm.db.rule_weeks", { n: r.every, day: t(`adm.db.wd.${r.weekday}`), time });
    return t("adm.db.rule_months", { n: r.every, day: r.day, time });
  };

  const kindOf = (key) => {
    if (key.startsWith("pre-restore/")) return t("adm.db.kind_pre");
    if (key.startsWith("now/")) return t("adm.db.kind_now");
    const rule = status?.rules.find(r => key.startsWith(`auto/${r.id}/`));
    return rule ? describe(rule) : t("adm.db.kind_auto");
  };

  const formValid = form && /^\d{2}:\d{2}$/.test(form.time) && inRange(form.every, 1, MAX_EVERY[form.unit]) && inRange(form.keep, 1, 365)
    && (form.unit !== "month" || inRange(form.day, 1, 28));
  const canApply = preview && status?.maintenance && status?.bucket_ready && preview.has_tools_user;

  if (!status) return null;

  return (
    <div className="space-y-3">
      {/* Maintenance */}
      <div className={`${card} ${status.maintenance ? "border-amber-300 bg-amber-50" : ""}`}>
        <div className="flex items-center justify-between gap-3 flex-wrap">
          <div>
            <p className="font-semibold text-slate-900 text-sm flex items-center gap-1.5"><Wrench className="w-4 h-4" /> {t("adm.db.maint_title")}</p>
            <p className="text-sm text-slate-600">{t(status.maintenance ? "adm.db.maint_on" : "adm.db.maint_off")}</p>
          </div>
          <Button size="sm" variant={status.maintenance ? "default" : "outline"} disabled={busy}
            onClick={() => setMaintenance(!status.maintenance)}
            className={status.maintenance ? "bg-emerald-600 hover:bg-emerald-700" : ""}>
            {t(status.maintenance ? "adm.db.maint_turn_off" : "adm.db.maint_turn_on")}
          </Button>
        </div>
      </div>

      {/* Manual export */}
      <div className={card}>
        <p className="font-semibold text-slate-900 text-sm">{t("adm.db.export_title")}</p>
        <p className="text-sm text-slate-600">{t("adm.db.export_intro")}</p>
        <div className="flex gap-2 flex-wrap items-end">
          <div className="space-y-1.5 flex-1 min-w-[12rem]">
            <Label htmlFor="db-export-password">{t("adm.db.your_password")}</Label>
            <Input id="db-export-password" type="password" autoComplete="current-password" value={exportPassword}
              onChange={e => setExportPassword(e.target.value)} className="h-10" />
          </div>
          <Button onClick={exportZip} disabled={busy || !exportPassword} className="gap-1.5 bg-blue-600 hover:bg-blue-700">
            <Download className="w-4 h-4" /> {t("adm.db.export")}
          </Button>
        </div>
      </div>

      {/* Restore */}
      <div className={card}>
        <p className="font-semibold text-slate-900 text-sm">{t("adm.db.restore_title")}</p>
        <p className="text-sm text-slate-600">{t("adm.db.restore_intro")}</p>
        <div className="grid gap-3 sm:grid-cols-3">
          <div className="space-y-1.5">
            <Label htmlFor="db-restore-file">{t("adm.db.zip_file")}</Label>
            <Input id="db-restore-file" type="file" accept=".zip,application/zip" className="h-10"
              onChange={e => { setRestore(r => ({ ...r, file: e.target.files?.[0] || null })); setPreview(null); }} />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="db-restore-password">{t("adm.db.your_password")}</Label>
            <Input id="db-restore-password" type="password" autoComplete="current-password" value={restore.password}
              onChange={e => setRestore(r => ({ ...r, password: e.target.value }))} className="h-10" />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="db-restore-zip-password">{t("adm.db.zip_password")}</Label>
            <Input id="db-restore-zip-password" type="password" autoComplete="off" value={restore.zipPassword}
              placeholder={t("adm.db.zip_password_hint")}
              onChange={e => setRestore(r => ({ ...r, zipPassword: e.target.value }))} className="h-10" />
          </div>
        </div>
        <div className="flex justify-end">
          <Button variant="outline" onClick={runPreview} disabled={busy || !restore.file || !restore.password} className="gap-1.5">
            <Upload className="w-4 h-4" /> {t("adm.db.preview")}
          </Button>
        </div>

        {preview && (
          <div className="space-y-2">
            <div className="rounded-xl border border-slate-200 divide-y divide-slate-50 text-sm">
              <div className="grid grid-cols-3 px-3 py-1.5 text-xs font-semibold text-slate-500">
                <span>{t("adm.db.collection")}</span><span className="text-right">{t("adm.db.now")}</span><span className="text-right">{t("adm.db.after")}</span>
              </div>
              {preview.rows.map(row => (
                <div key={row.name} className={`grid grid-cols-3 px-3 py-1.5 ${row.now > 0 && row.after === 0 ? "bg-red-50 text-red-800" : row.after < row.now ? "bg-amber-50" : ""}`}>
                  <span className="font-mono truncate">{row.name}</span>
                  <span className="text-right">{row.now}</span>
                  <span className="text-right font-semibold">{row.after}</span>
                </div>
              ))}
            </div>
            {preview.rows.some(r => r.now > 0 && r.after === 0) && <p className="text-xs text-red-700">{t("adm.db.emptied_note")}</p>}
            {!preview.has_tools_user && <p className="text-xs text-red-700">{t("adm.db.no_tools_user")}</p>}
            {!status.maintenance && <p className="text-xs text-amber-700">{t("adm.db.needs_maintenance")}</p>}
            {!status.bucket_ready && <p className="text-xs text-amber-700">{t("adm.db.needs_bucket")}</p>}
            <div className="flex justify-end">
              <Button onClick={() => setConfirmOpen(true)} disabled={busy || !canApply} className="gap-1.5 bg-red-600 hover:bg-red-700">
                <DatabaseBackup className="w-4 h-4" /> {t("adm.db.replace")}
              </Button>
            </div>
          </div>
        )}
      </div>

      {/* Automatic backups */}
      <div className={card}>
        <div className="flex items-center justify-between gap-2 flex-wrap">
          <p className="font-semibold text-slate-900 text-sm">{t("adm.db.auto_title")}</p>
          <div className="flex gap-2">
            <Button size="sm" variant="outline" onClick={backupNow} disabled={busy || !status.bucket_ready}>{t("adm.db.backup_now")}</Button>
            <Button size="sm" onClick={() => openRule(null)} className="gap-1.5 bg-blue-600 hover:bg-blue-700">
              <Plus className="w-4 h-4" /> {t("adm.db.add_rule")}
            </Button>
          </div>
        </div>
        {!status.bucket_ready && <p className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-xl px-3 py-2">{t("adm.db.needs_bucket")}</p>}
        <p className="text-xs text-slate-500">{t("adm.db.auto_intro")}</p>
        <div className="rounded-xl border border-slate-200 divide-y divide-slate-50">
          {status.rules.length === 0 && <p className="p-6 text-center text-slate-400 text-sm">{t("adm.db.no_rules")}</p>}
          {status.rules.map(r => (
            <div key={r.id} className="flex items-center gap-3 px-3 py-2 flex-wrap">
              <div className="flex-1 min-w-[12rem]">
                <p className={`text-sm font-semibold ${r.enabled ? "text-slate-900" : "text-slate-400"}`}>
                  {describe(r)} <span className="font-normal text-xs text-slate-500">· {t("adm.db.keep_n", { n: r.keep })}</span>
                </p>
                <p className="text-xs text-slate-500">
                  {t("adm.db.last_run")}: {r.last_status === "error"
                    ? <span className="text-red-700">✗ {when(r.last_run_at)} — {r.last_error}</span>
                    : r.last_status === "ok" ? <span className="text-emerald-700">✓ {when(r.last_run_at)}</span> : "—"}
                  {" · "}{t("adm.db.next_run")}: {r.enabled ? when(r.next_run_at) : t("adm.inactive")}
                </p>
              </div>
              <Button variant="ghost" size="sm" className="h-7 w-7 p-0 text-slate-400 hover:text-blue-600" onClick={() => openRule(r)} aria-label={t("common.edit")}>
                <Pencil className="w-3.5 h-3.5" />
              </Button>
              <Button variant="ghost" size="sm" className="h-7 w-7 p-0 text-slate-400 hover:text-red-600" onClick={() => deleteRule(r)} aria-label={t("common.delete")}>
                <Trash2 className="w-3.5 h-3.5" />
              </Button>
            </div>
          ))}
        </div>
        <p className="text-xs text-slate-400">{t("adm.db.timezone")}</p>
      </div>

      {/* Latest backups in the bucket */}
      <div className={card}>
        <p className="font-semibold text-slate-900 text-sm">{t("adm.db.files_title")}</p>
        {status.files_error && <p className="text-xs text-red-700">{status.files_error}</p>}
        <div className="rounded-xl border border-slate-200 divide-y divide-slate-50">
          {status.files.length === 0 && <p className="p-6 text-center text-slate-400 text-sm">{t("adm.db.no_files")}</p>}
          {status.files.map(f => (
            <div key={f.key} className="flex items-center gap-3 px-3 py-2 text-sm">
              <span className="w-40 shrink-0 text-slate-900">{when(f.modified)}</span>
              <span className="flex-1 min-w-0 truncate text-slate-600">{kindOf(f.key)}</span>
              <span className="text-xs text-slate-400">{Math.max(1, Math.round(f.size / 1024))} KB</span>
              <Button variant="ghost" size="sm" className="h-7 w-7 p-0 text-slate-400 hover:text-blue-600" disabled={busy}
                onClick={() => download(f.key)} aria-label={t("adm.db.download")}>
                <Download className="w-3.5 h-3.5" />
              </Button>
            </div>
          ))}
        </div>
        <p className="text-xs text-slate-500">{t("adm.db.files_note")}</p>
      </div>

      <FileBackupPanel showMsg={showMsg} refreshTick={refreshTick} />

      <Dialog open={!!form} onOpenChange={(open) => !open && setForm(null)}>
        <DialogContent className="max-w-md">
          <DialogHeader><DialogTitle>{t(editingId ? "adm.db.edit_rule" : "adm.db.add_rule")}</DialogTitle></DialogHeader>
          {form && (
            <div className="space-y-3">
              <div className="flex items-center gap-2">
                <Switch id="rule-enabled" checked={form.enabled} onCheckedChange={v => setForm(f => ({ ...f, enabled: v }))} />
                <Label htmlFor="rule-enabled">{t("adm.active")}</Label>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1.5">
                  <Label>{t("adm.db.unit")}</Label>
                  <Select value={form.unit} onValueChange={v => setForm(f => ({ ...f, unit: v }))}>
                    <SelectTrigger className="h-10"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {["day", "week", "month"].map(u => <SelectItem key={u} value={u}>{t(`adm.db.unit_${u}`)}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-1.5">
                  <Label htmlFor="rule-every">{t(`adm.db.every_${form.unit}`)}</Label>
                  <Input id="rule-every" type="number" min={1} max={MAX_EVERY[form.unit]} value={form.every} className="h-10"
                    onChange={e => setForm(f => ({ ...f, every: e.target.value }))} />
                </div>
                {form.unit === "week" && (
                  <div className="space-y-1.5">
                    <Label>{t("adm.db.weekday")}</Label>
                    <Select value={String(form.weekday)} onValueChange={v => setForm(f => ({ ...f, weekday: Number(v) }))}>
                      <SelectTrigger className="h-10"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        {[0, 1, 2, 3, 4, 5, 6].map(d => <SelectItem key={d} value={String(d)}>{t(`adm.db.wd.${d}`)}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                )}
                {form.unit === "month" && (
                  <div className="space-y-1.5">
                    <Label htmlFor="rule-day">{t("adm.db.month_day")}</Label>
                    <Input id="rule-day" type="number" min={1} max={28} value={form.day} className="h-10"
                      onChange={e => setForm(f => ({ ...f, day: e.target.value }))} />
                  </div>
                )}
                <div className="space-y-1.5">
                  <Label htmlFor="rule-time">{t("adm.db.time")}</Label>
                  <Input id="rule-time" type="time" value={form.time} className="h-10"
                    onChange={e => setForm(f => ({ ...f, time: e.target.value }))} />
                </div>
                <div className="space-y-1.5">
                  <Label htmlFor="rule-keep">{t("adm.db.keep")}</Label>
                  <Input id="rule-keep" type="number" min={1} max={365} value={form.keep} className="h-10"
                    onChange={e => setForm(f => ({ ...f, keep: e.target.value }))} />
                </div>
              </div>
              <p className="text-xs text-slate-500">{formValid ? describe({ ...form, every: Number(form.every) }) : t("adm.db.rule_invalid")}</p>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setForm(null)}>{t("common.cancel")}</Button>
            <Button onClick={saveRule} disabled={busy || !formValid} className="bg-blue-600 hover:bg-blue-700">{t("common.save")}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AlertDialog open={confirmOpen} onOpenChange={setConfirmOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t("adm.db.confirm_title")}</AlertDialogTitle>
            <p className="text-sm text-muted-foreground">{t("adm.db.confirm_body")}</p>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>{t("common.cancel")}</AlertDialogCancel>
            <AlertDialogAction className="bg-red-600 hover:bg-red-700" onClick={applyRestore}>{t("adm.db.replace")}</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
